import {useState, useEffect, useCallback} from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import axios from 'axios';
import toast from 'react-hot-toast';
import { Key, Plus, Trash2, Copy, X, Code, Eye, Edit, CheckCircle, Activity, Calendar, Clock, AlertTriangle, BarChart3, Info, ChevronLeft, ChevronRight, Cpu, Type, ImageIcon, Video, FileText, Layers, FileCode, Terminal } from 'lucide-react';
import { codeExamples, CodeExample } from '../../constants/apiExamples';
import { CONFIG_API_KEY_PLACEHOLDER } from '../../constants/ideConfigs';
import ConfigExportModal from './components/ConfigExportModal';
import { Model } from '../../types/chat';
import { formatDate, formatDateTime } from '../../utils/dateFormat';
import { formatCompactNumber } from '../../utils/formatNumber';
import { getModelBrandIcon } from '../../utils/modelBrandIcon';

interface UsageLog {
  timestamp: string;
  model: string;
  reference?: string;
  usage: {
    prompt_tokens: number;
    completion_tokens: number;
    total_tokens: number;
    is_byok: boolean;
    prompt_tokens_details: {
      cached_tokens: number;
      audio_tokens: number;
      video_tokens: number;
    };
    completion_tokens_details: {
      reasoning_tokens: number;
      image_tokens: number;
    };
  };
}

interface ApiKey {
  id: string;
  name: string;
  usage: {
    totalRequests: number;
    monthlyRequests: number;
    dailyRequests: number;
  };
  usageLogs: UsageLog[];
  tokenUsage: {
    promptTokens: number;
    completionTokens: number;
    totalTokens: number;
  };
  isActive: boolean;
  expiresAt?: string;
  lastUsedAt?: string;
  createdAt: string;
  maskedKey: string;
}

type GeneratedApiKey = ApiKey & { key: string };

import PageTitle from '../../components/PageTitle';

export default function ApiKeys() {
  const { t } = useTranslation(['settings', 'common']);
  const location = useLocation();
  const [apiKeys, setApiKeys] = useState<ApiKey[]>([]);
  const [loading, setLoading] = useState(false);
  const [creating, setCreating] = useState(false);
  const [newKeyName, setNewKeyName] = useState('');
  const [newKeyExpiration, setNewKeyExpiration] = useState('');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showGeneratedKeyModal, setShowGeneratedKeyModal] = useState(false);
  const [generatedKey, setGeneratedKey] = useState<GeneratedApiKey | null>(null);
  const [configExport, setConfigExport] = useState<{ apiKey: string; isRealKey: boolean; initialMode?: 'files' | 'script' } | null>(null);
  const [nameError, setNameError] = useState('');
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deletingKey, setDeletingKey] = useState<{ id: string; name: string } | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [showRevealModal, setShowRevealModal] = useState(false);
  const [revealingApiKey, setRevealingApiKey] = useState<ApiKey | null>(null);
  const [revealPassword, setRevealPassword] = useState('');
  const [revealedFullKey, setRevealedFullKey] = useState('');
  const [revealing, setRevealing] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedApiKey, setSelectedApiKey] = useState<ApiKey | null>(null);
  const [activeLanguageTab, setActiveLanguageTab] = useState<keyof CodeExample>('nodejs');
  const [activeExampleTab, setActiveExampleTab] = useState<keyof CodeExample['nodejs']>('basic');
  const [usageLogsPage, setUsageLogsPage] = useState(1);
  const [usageLogsPerPage] = useState(5);
  const [editingName, setEditingName] = useState('');
  const [editingStatus, setEditingStatus] = useState(false);
  const [saving, setSaving] = useState(false);
  const [models, setModels] = useState<Model[]>([]);
  const [modelsLoading, setModelsLoading] = useState(false);

  useEffect(() => {
    loadApiKeys();
  }, [location.pathname]);

  useEffect(() => {
    const loadModels = async () => {
      setModelsLoading(true);
      try {
        const response = await axios.get('/api/models');
        const allModels = response.data?.models || response.data?.data || [];
        setModels(allModels);
      } catch (error) {
        console.error('Failed to load models:', error);
      } finally {
        setModelsLoading(false);
      }
    };
    loadModels();
  }, []);

  useEffect(() => {
    if (showViewModal || showEditModal || showRevealModal) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => { document.body.style.overflow = ''; };
  }, [showViewModal, showEditModal, showRevealModal]);

  const loadApiKeys = useCallback(async () => {
    // Prevent multiple simultaneous requests
    if (loading) return;

    try {
      setLoading(true);
      const response = await axios.get('/api/auth/user/api-keys');

      // Automatically set expired keys to inactive
      const processedApiKeys = response.data.map((key: any) => ({
        ...key,
        isActive: key.expiresAt && new Date(key.expiresAt) < new Date() ? false : key.isActive
      }));

      setApiKeys(processedApiKeys);
    } catch (error) {
      console.error('Failed to load API keys:', error);
      toast.error(t('api_keys.load_failed'));
    } finally {
      setLoading(false);
    }
  }, [loading]);

  const createApiKey = async () => {
    if (!newKeyName.trim()) {
      toast.error(t('api_keys.name_required'));
      return;
    }

    // Check for duplicate names locally
    const trimmedName = newKeyName.trim();
    const existingKey = apiKeys.find(key => key.name.toLowerCase() === trimmedName.toLowerCase());
    if (existingKey) {
      toast.error(t('api_keys.name_exists'));
      return;
    }

    // Validate expiration date if provided
    let expirationDate: string | undefined;
    if (newKeyExpiration) {
      const parsedDate = new Date(newKeyExpiration);
      if (isNaN(parsedDate.getTime())) {
        toast.error(t('api_keys.invalid_date'));
        return;
      }

      if (parsedDate <= new Date()) {
        toast.error(t('api_keys.date_future'));
        return;
      }

      expirationDate = parsedDate.toISOString();
    }

    try {
      setCreating(true);
      const response = await axios.post('/api/auth/user/api-keys', {
        name: newKeyName.trim(),
        expiresAt: expirationDate
      });

      const createdApiKey = response.data as GeneratedApiKey;
      setApiKeys(prev => [createdApiKey, ...prev]);
      setGeneratedKey(createdApiKey);
      setNewKeyName('');
      setNewKeyExpiration('');
      setNameError('');
      setShowCreateModal(false);
      setShowGeneratedKeyModal(true);
    } catch (error: any) {
      console.error('Failed to create API key:', error);
      toast.error(error.response?.data?.error || t('api_keys.create_failed'));
    } finally {
      setCreating(false);
    }
  };

  const handleDeleteClick = (keyId: string, keyName: string) => {
    setDeletingKey({ id: keyId, name: keyName });
    setShowDeleteModal(true);
  };

  const handleDetailsClick = useCallback((apiKey: ApiKey) => {
    setSelectedApiKey(apiKey);
    setUsageLogsPage(1); // Reset to first page when opening details
    setShowViewModal(true);
  }, []);

  const handleDirectEdit = (apiKey: ApiKey) => {
    setSelectedApiKey(apiKey);
    setEditingName(apiKey.name);
    setEditingStatus(apiKey.isActive);
    setUsageLogsPage(1); // Reset to first page when opening details
    setShowEditModal(true);
  };

  const openRevealModal = (apiKey: ApiKey) => {
    setRevealingApiKey(apiKey);
    setRevealPassword('');
    setRevealedFullKey('');
    setShowRevealModal(true);
  };

  const closeRevealModal = () => {
    setShowRevealModal(false);
    setRevealingApiKey(null);
    setRevealPassword('');
    setRevealedFullKey('');
    setRevealing(false);
  };

  const revealFullKey = async () => {
    if (!revealingApiKey) return;

    if (!revealPassword) {
      toast.error(t('api_keys.password_required'));
      return;
    }

    setRevealing(true);
    try {
      const response = await axios.post(`/api/auth/user/api-keys/${revealingApiKey.id}/reveal`, {
        password: revealPassword
      });
      setRevealedFullKey(response.data.key);
      setRevealPassword('');
    } catch (error: any) {
      toast.error(error.response?.data?.error || t('api_keys.reveal_failed'));
    } finally {
      setRevealing(false);
    }
  };

  const cancelEditing = () => {
    setShowEditModal(false);
    setEditingName('');
    setEditingStatus(false);
  };

  const saveChanges = async () => {
    if (!selectedApiKey) return;

    // Validate name
    const trimmedName = editingName.trim();
    if (!trimmedName) {
      toast.error(t('api_keys.name_empty'));
      return;
    }

    // Check for duplicate names locally
    const existingKey = apiKeys.find(key =>
      key.id !== selectedApiKey.id &&
      key.name.toLowerCase() === trimmedName.toLowerCase()
    );
    if (existingKey) {
      toast.error(t('api_keys.name_exists'));
      return;
    }

    // For expired keys, always set status to inactive and don't allow changes
    const isExpired = selectedApiKey.expiresAt && new Date(selectedApiKey.expiresAt) < new Date();
    const finalStatus = isExpired ? false : editingStatus;

    setSaving(true);
    try {
      await axios.patch(`/api/auth/user/api-keys/${selectedApiKey.id}`, {
        name: trimmedName,
        isActive: finalStatus
      });

      // Update local state
      setApiKeys(prev => prev.map(key =>
        key.id === selectedApiKey.id
          ? { ...key, name: trimmedName, isActive: finalStatus }
          : key
      ));

      // Update selected API key
      setSelectedApiKey(prev => prev ? { ...prev, name: trimmedName, isActive: finalStatus } : null);

      // Keep loaded state since we updated locally
      setShowEditModal(false);
      toast.success(t('api_keys.update_success'));
    } catch (error: any) {
      console.error('Failed to update API key:', error);
      toast.error(error.response?.data?.error || t('api_keys.update_failed'));
    } finally {
      setSaving(false);
    }
  };

  const confirmDeleteApiKey = async () => {
    if (!deletingKey) return;

    setDeleting(true);
    try {
      await axios.delete(`/api/auth/user/api-keys/${deletingKey.id}`);
      setApiKeys(prev => prev.filter(key => key.id !== deletingKey.id));
      toast.success(t('api_keys.delete_success'));
      setShowDeleteModal(false);
      setDeletingKey(null);
    } catch (error) {
      console.error('Failed to delete API key:', error);
      toast.error(t('api_keys.delete_failed'));
    } finally {
      setDeleting(false);
    }
  };

  const cancelDelete = () => {
    setShowDeleteModal(false);
    setDeletingKey(null);
  };

  const copyToClipboard = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success(t('common:copied_to_clipboard'));
    } catch (error) {
      // Fallback for older browsers or insecure contexts
      const textArea = document.createElement('textarea');
      textArea.value = text;
      document.body.appendChild(textArea);
      textArea.select();
      try {
        document.execCommand('copy');
        toast.success(t('common:copied_to_clipboard'));
      } catch (fallbackError) {
        toast.error(t('common:failed_to_copy'));
      }
      document.body.removeChild(textArea);
    }
  };


  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="w-7 h-7 border-2 border-primary border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      <PageTitle 
        titleKey="api_keys.title" 
        namespace="settings" 
        descriptionKey="api_keys.subtitle"
      />
      {/* Section Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-semibold text-foreground tracking-[-0.04em]">{t('api_keys.title')}</h2>
          <p className="mt-1 text-sm text-muted-foreground">
            {t('api_keys.subtitle')}
          </p>
        </div>
        <button
          onClick={() => setShowCreateModal(true)}
          className="flex items-center gap-2 px-4 py-2.5 bg-primary text-primary-foreground rounded-xl hover:bg-primary/90 transition-colors font-medium text-sm"
        >
          <Plus className="h-4 w-4" />
          {t('api_keys.create_key')}
        </button>
      </div>

      {/* Stats Overview */}
      {apiKeys.length > 0 && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="bg-card rounded-xl p-4 border border-border shadow-sm">
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 bg-primary/10 rounded-xl flex items-center justify-center">
                <Key className="h-4 w-4 text-primary" />
              </div>
              <div>
                <div className="text-lg font-bold text-foreground">{apiKeys.length}</div>
                <div className="text-xs font-medium text-muted-foreground uppercase tracking-wider">{t('api_keys.total_keys')}</div>
              </div>
            </div>
          </div>
          <div className="bg-card rounded-xl p-4 border border-border shadow-sm">
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 bg-emerald-500/10 rounded-xl flex items-center justify-center">
                <CheckCircle className="h-4 w-4 text-emerald-500" />
              </div>
              <div>
                <div className="text-lg font-bold text-foreground">
                  {apiKeys.filter(k => k.isActive && !(k.expiresAt && new Date(k.expiresAt) < new Date())).length}
                </div>
                <div className="text-xs font-medium text-muted-foreground uppercase tracking-wider">{t('api_keys.active')}</div>
              </div>
            </div>
          </div>
          <div className="bg-card rounded-xl p-4 border border-border shadow-sm">
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 bg-purple-500/10 rounded-xl flex items-center justify-center">
                <Activity className="h-4 w-4 text-purple-500" />
              </div>
              <div>
                <div className="text-lg font-bold text-foreground">
                  {formatCompactNumber(apiKeys.reduce((sum, k) => sum + (k.usage?.totalRequests || 0), 0))}
                </div>
                <div className="text-xs font-medium text-muted-foreground uppercase tracking-wider">{t('api_keys.total_requests')}</div>
              </div>
            </div>
          </div>
          <div className="bg-card rounded-xl p-4 border border-border shadow-sm">
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 bg-amber-500/10 rounded-xl flex items-center justify-center">
                <BarChart3 className="h-4 w-4 text-amber-500" />
              </div>
              <div>
                <div className="text-lg font-bold text-foreground">
                  {formatCompactNumber(apiKeys.reduce((sum, k) => sum + (k.usage?.monthlyRequests || 0), 0))}
                </div>
                <div className="text-xs font-medium text-muted-foreground uppercase tracking-wider">{t('api_keys.monthly_requests')}</div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Create API Key Modal */}
      {showCreateModal && (
        <div 
          className="fixed inset-0 bg-black/50 flex items-center justify-center z-50"
          onKeyDown={e => {
            if (e.key === 'Escape') {
              setShowCreateModal(false);
              setNewKeyName('');
              setNewKeyExpiration('');
              setNameError('');
            }
          }}
          tabIndex={-1}
        >
          <div className="bg-card border border-border rounded-lg shadow-lg max-w-md w-full mx-4">
            <div className="p-6">
              <div className="flex items-center gap-3 mb-5">
                <div className="p-2.5 rounded-xl bg-primary/10">
                  <Key className="h-5 w-5 text-primary" />
                </div>
                <div className="flex-1">
                  <h3 className="text-lg font-semibold text-foreground tracking-[-0.04em]">
                    {t('api_keys.create_title')}
                  </h3>
                  <p className="text-sm text-muted-foreground mt-0.5">
                    {t('api_keys.create_subtitle')}
                  </p>
                </div>
                <button
                  onClick={() => {
                    setShowCreateModal(false);
                    setNewKeyName('');
                    setNewKeyExpiration('');
                    setNameError('');
                  }}
                  className="p-1.5 text-muted-foreground hover:text-foreground hover:bg-muted rounded-lg transition-colors"
                >
                  <X className="h-4 w-4" />
                </button>
              </div>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">
                    {t('api_keys.key_name')} <span className="text-destructive">*</span>
                  </label>
                  <input
                    type="text"
                    placeholder={t('api_keys.key_name_placeholder')}
                    value={newKeyName}
                    onChange={(e) => {
                      setNewKeyName(e.target.value);
                      if (nameError) setNameError('');
                    }}
                    onBlur={() => {
                      const trimmed = newKeyName.trim();
                      if (trimmed) {
                        const existing = apiKeys.find(key =>
                          key.name.toLowerCase() === trimmed.toLowerCase()
                        );
                        if (existing) {
                          setNameError(t('api_keys.name_exists'));
                        }
                      }
                    }}
                    onKeyPress={(e) => e.key === 'Enter' && !creating && newKeyName.trim() && !nameError && createApiKey()}
                    className={`w-full px-4 py-2.5 border rounded-xl bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent transition-all ${
                      nameError ? 'border-destructive' : 'border-border'
                    }`}
                    maxLength={50}
                    autoFocus
                  />
                  {nameError && (
                    <p className="text-sm text-destructive mt-1.5">{nameError}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">
                    {t('api_keys.expiration_date')} <span className="text-muted-foreground">({t('common:optional')})</span>
                  </label>
                  <input
                    type="datetime-local"
                    value={newKeyExpiration}
                    onChange={(e) => setNewKeyExpiration(e.target.value)}
                    min={new Date(Date.now() + 60000).toISOString().slice(0, 16)}
                    className="w-full px-4 py-2.5 border border-border rounded-xl bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent transition-all"
                  />
                  <p className="text-xs text-muted-foreground mt-1.5">
                    {t('api_keys.expiration_hint')}
                  </p>
                </div>
              </div>

              <div className="flex gap-3 justify-end mt-6">
                <button
                  onClick={() => {
                    setShowCreateModal(false);
                    setNewKeyName('');
                    setNewKeyExpiration('');
                  }}
                  disabled={creating}
                  className="px-4 py-2.5 text-sm font-medium text-muted-foreground hover:text-foreground rounded-xl hover:bg-muted transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {t('common:cancel')}
                </button>
                <button
                  onClick={createApiKey}
                  disabled={creating || !newKeyName.trim() || !!nameError}
                  className="px-4 py-2.5 text-sm font-medium bg-primary text-primary-foreground rounded-xl hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center gap-2"
                >
                  {creating ? (
                    <div className="w-4 h-4 border-2 border-primary-foreground border-t-transparent rounded-full animate-spin"></div>
                  ) : (
                    <Plus className="h-4 w-4" />
                  )}
                  {t('api_keys.create_button')}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Generated Key Modal */}
      {showGeneratedKeyModal && generatedKey && (
        <div 
          className="fixed inset-0 bg-black/50 flex items-center justify-center z-50"
          onKeyDown={e => {
            if (e.key === 'Escape') {
              setShowGeneratedKeyModal(false);
              setGeneratedKey(null);
            }
          }}
          tabIndex={-1}
        >
          <div className="bg-card border border-border rounded-lg shadow-lg max-w-md w-full mx-4">
            <div className="p-6">
              <div className="flex items-center gap-3 mb-5">
                <div className="p-2.5 rounded-xl bg-emerald-500/10">
                  <Key className="h-5 w-5 text-emerald-500" />
                </div>
                <div className="flex-1">
                  <h3 className="text-lg font-semibold text-foreground tracking-[-0.04em]">
                    {t('api_keys.created_title')}
                  </h3>
                  <p className="text-sm text-muted-foreground mt-0.5">
                    {t('api_keys.created_subtitle')}
                  </p>
                </div>
                <button
                  onClick={() => {
                    setShowGeneratedKeyModal(false);
                    setGeneratedKey(null);
                  }}
                  className="p-1.5 text-muted-foreground hover:text-foreground hover:bg-muted rounded-lg transition-colors"
                >
                  <X className="h-4 w-4" />
                </button>
              </div>

              <div className="mb-6">
                <div className="p-4 bg-amber-500/10 border border-amber-500/20 rounded-xl mb-4">
                  <div className="flex items-start gap-2.5">
                    <AlertTriangle className="h-5 w-5 text-amber-500 flex-shrink-0 mt-0.5" />
                    <div className="flex-1">
                      <p className="text-sm font-medium text-amber-600 dark:text-amber-400">
                        {t('api_keys.important')}
                      </p>
                      <p className="text-sm text-amber-600/80 dark:text-amber-400/80 mt-1">
                        {t('api_keys.copy_warning')}
                      </p>
                    </div>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">
                    {t('api_keys.your_key')}
                  </label>
                  <div className="flex items-center gap-2">
                    <code className="flex-1 px-4 py-3 bg-muted border border-border rounded-xl text-sm font-mono break-all select-all text-foreground">
                      {generatedKey.key}
                    </code>
                    <button
                      onClick={() => copyToClipboard(generatedKey.key)}
                      className="p-2.5 text-muted-foreground hover:text-foreground hover:bg-muted rounded-lg transition-colors"
                      title={t('api_keys.copy_key')}
                    >
                      <Copy className="h-4 w-4" />
                    </button>
                  </div>
                </div>
              </div>

              <div className="flex justify-end gap-2">
                <button
                  onClick={() => setConfigExport({ apiKey: generatedKey.key, isRealKey: true, initialMode: 'files' })}
                  className="px-4 py-2.5 text-sm font-medium bg-card border border-border text-foreground rounded-xl hover:bg-muted transition-colors flex items-center gap-2"
                >
                  <FileCode className="h-4 w-4" />
                  {t('api_keys.config_export.export_button')}
                </button>
                <button
                  onClick={() => copyToClipboard(generatedKey.key)}
                  className="px-4 py-2.5 text-sm font-medium bg-emerald-600 text-white rounded-xl hover:bg-emerald-700 transition-colors flex items-center gap-2"
                >
                  <Copy className="h-4 w-4" />
                  {t('api_keys.copied_key')}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Delete API Key Modal */}
      {showDeleteModal && deletingKey && (
        <div 
          className="fixed inset-0 bg-black/50 flex items-center justify-center z-50"
          onKeyDown={e => e.key === 'Escape' && cancelDelete()}
          tabIndex={-1}
        >
          <div className="bg-card border border-border rounded-lg shadow-lg max-w-md w-full mx-4">
            <div className="p-6">
              <div className="flex items-center gap-3 mb-5">
                <div className="p-2.5 rounded-xl bg-destructive/10">
                  <Trash2 className="h-5 w-5 text-destructive" />
                </div>
                <div className="flex-1">
                  <h3 className="text-lg font-semibold text-foreground tracking-[-0.04em]">
                    {t('api_keys.delete_title')}
                  </h3>
                  <p className="text-sm text-muted-foreground mt-0.5">
                    {t('api_keys.delete_warning')}
                  </p>
                </div>
                <button
                  onClick={cancelDelete}
                  className="p-1.5 text-muted-foreground hover:text-foreground hover:bg-muted rounded-lg transition-colors"
                >
                  <X className="h-4 w-4" />
                </button>
              </div>

              <div className="mb-6">
                <p className="text-sm text-foreground">
                  {t('api_keys.delete_confirm', { name: deletingKey.name })}
                </p>
                <div className="mt-3 p-3 bg-destructive/5 border border-destructive/20 rounded-xl">
                  <div className="flex items-start gap-2.5">
                    <AlertTriangle className="h-5 w-5 text-destructive flex-shrink-0 mt-0.5" />
                    <div className="flex-1">
                      <p className="text-sm font-medium text-destructive">
                        {t('common:warning')}
                      </p>
                      <p className="text-sm text-destructive/80 mt-1">
                        {t('api_keys.delete_app_warning')}
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="flex gap-3 justify-end">
                <button
                  onClick={cancelDelete}
                  disabled={deleting}
                  className="px-4 py-2.5 text-sm font-medium text-muted-foreground hover:text-foreground rounded-xl hover:bg-muted transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {t('common:cancel')}
                </button>
                <button
                  onClick={confirmDeleteApiKey}
                  disabled={deleting}
                  className="px-4 py-2.5 text-sm font-medium bg-destructive text-destructive-foreground rounded-xl hover:bg-destructive/90 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center gap-2"
                >
                  {deleting ? (
                    <>
                      <div className="w-4 h-4 border-2 border-destructive-foreground border-t-transparent rounded-full animate-spin"></div>
                      {t('common:deleting')}
                    </>
                  ) : (
                    <>
                      <Trash2 className="h-4 w-4" />
                      {t('api_keys.delete_button')}
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}


      {/* API Keys List */}
      <div>
        {apiKeys.length === 0 ? (
          <div className="text-center py-16 rounded-xl border-2 border-dashed border-border bg-card/50">
            <div className="w-12 h-12 bg-primary/10 rounded-xl flex items-center justify-center mx-auto mb-3">
              <Key className="h-6 w-6 text-primary" />
            </div>
            <h3 className="text-[15px] font-semibold text-foreground tracking-[-0.04em] mb-1.5">{t('api_keys.no_keys')}</h3>
            <p className="text-[13px] text-muted-foreground mb-5 max-w-sm mx-auto">
              {t('api_keys.no_keys_desc')}
            </p>
            <button
              onClick={() => setShowCreateModal(true)}
              className="inline-flex items-center gap-2 px-4 py-2 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 active:scale-[0.98] transition-all text-[13px] font-medium"
            >
              <Plus className="h-3.5 w-3.5" />
              {t('api_keys.create_first')}
            </button>
          </div>
        ) : (
          <div className="rounded-lg border border-border bg-card overflow-hidden shadow-xs">
            {/* List header */}
            <div className="px-4 py-2.5 bg-muted/40 border-b border-border flex items-center justify-between">
              <span className="text-[12px] font-medium text-muted-foreground uppercase tracking-wider">
                {t('api_keys.total_keys')}
              </span>
              <span className="text-[12px] font-medium text-muted-foreground tabular-nums">
                {apiKeys.length}
              </span>
            </div>

            {/* List rows */}
            <div className="divide-y divide-border">
              {apiKeys.map((apiKey) => {
                const isExpired = apiKey.expiresAt && new Date(apiKey.expiresAt) < new Date();

                return (
                  <div
                    key={apiKey.id}
                    className={`group px-4 py-3.5 transition-colors duration-150 ${
                      isExpired
                        ? 'bg-destructive/[0.03] hover:bg-destructive/[0.06]'
                        : apiKey.isActive
                          ? 'hover:bg-muted/50'
                          : 'opacity-60 hover:opacity-80 hover:bg-muted/30'
                    }`}
                  >
                    <div className="flex items-center gap-3.5">
                      {/* Status dot + Icon */}
                      <div className="relative flex-shrink-0">
                        <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${
                          isExpired
                            ? 'bg-destructive/10 text-destructive'
                            : apiKey.isActive
                              ? 'bg-primary/10 text-primary'
                              : 'bg-muted text-muted-foreground'
                        }`}>
                          <Key className="h-4 w-4" />
                        </div>
                        {/* Status indicator dot */}
                        <div className={`absolute -top-0.5 -right-0.5 w-2.5 h-2.5 rounded-full border-2 border-card ${
                          isExpired
                            ? 'bg-destructive'
                            : apiKey.isActive
                              ? 'bg-emerald-500'
                              : 'bg-muted-foreground/50'
                        }`} />
                      </div>

                      {/* Name + masked key */}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <span className="text-[14px] font-medium text-foreground truncate tracking-[-0.01em]">
                            {apiKey.name}
                          </span>
                          {isExpired ? (
                            <span className="inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wider text-destructive bg-destructive/10 px-1.5 py-0.5 rounded">
                              <span className="w-1.5 h-1.5 rounded-full bg-destructive" />
                              {t('api_keys.expired_badge')}
                            </span>
                          ) : (
                            <span className={`inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wider px-1.5 py-0.5 rounded ${
                              apiKey.isActive
                                ? 'text-emerald-600 dark:text-emerald-400 bg-emerald-500/10'
                                : 'text-muted-foreground bg-muted'
                            }`}>
                              <span className={`w-1.5 h-1.5 rounded-full ${apiKey.isActive ? 'bg-emerald-500' : 'bg-muted-foreground/50'}`} />
                              {apiKey.isActive ? t('api_keys.active') : t('api_keys.inactive_label')}
                            </span>
                          )}
                        </div>
                        <div className="flex items-center gap-2 mt-0.5">
                          <code className="text-[12px] font-mono text-muted-foreground">
                            {apiKey.maskedKey}
                          </code>
                        </div>
                      </div>

                      {/* Metadata — hidden on mobile */}
                      <div className="hidden md:flex items-center gap-6 flex-shrink-0">
                        <div className="text-center min-w-[5rem]">
                          <div className="text-[14px] font-semibold text-foreground tabular-nums">{formatDate(apiKey.createdAt)}</div>
                          <div className="text-[11px] text-muted-foreground">{t('api_keys.created_label')}</div>
                        </div>
                        {apiKey.expiresAt && (
                          <div className="text-center min-w-[5rem]">
                            <div className={`text-[14px] font-semibold tabular-nums ${isExpired ? 'text-destructive' : 'text-amber-500'}`}>{formatDate(apiKey.expiresAt)}</div>
                            <div className="text-[11px] text-muted-foreground">{t('api_keys.expires_label')}</div>
                          </div>
                        )}
                        <div className="text-center min-w-[8.5rem]">
                          <div className="text-[14px] font-semibold text-foreground tabular-nums">{apiKey.lastUsedAt ? formatDateTime(apiKey.lastUsedAt) : t('common:never')}</div>
                          <div className="text-[11px] text-muted-foreground">{t('api_keys.last_used_label')}</div>
                        </div>
                        <div className="text-center min-w-[4rem]">
                          <div className="text-[14px] font-semibold text-foreground tabular-nums">{formatCompactNumber(apiKey.usage?.totalRequests || 0)}</div>
                          <div className="text-[11px] text-muted-foreground">{t('api_keys.requests')}</div>
                        </div>
                      </div>

                      {/* Action buttons — appear on hover */}
                      <div className="flex items-center gap-0.5 flex-shrink-0 opacity-0 group-hover:opacity-100 transition-opacity duration-150">
                        <button
                          onClick={() => openRevealModal(apiKey)}
                          className="p-1.5 text-muted-foreground hover:text-emerald-500 hover:bg-emerald-500/10 rounded-md transition-colors"
                          title={t('api_keys.reveal_key')}
                        >
                          <Key className="h-3.5 w-3.5" />
                        </button>
                        <button
                          onClick={() => handleDetailsClick(apiKey)}
                          className="p-1.5 text-muted-foreground hover:text-primary hover:bg-primary/10 rounded-md transition-colors"
                          title={t('api_keys.view_details')}
                        >
                          <Eye className="h-3.5 w-3.5" />
                        </button>
                        <button
                          onClick={() => handleDirectEdit(apiKey)}
                          className="p-1.5 text-muted-foreground hover:text-purple-500 hover:bg-purple-500/10 rounded-md transition-colors"
                          title={t('api_keys.edit_title')}
                        >
                          <Edit className="h-3.5 w-3.5" />
                        </button>
                        <button
                          onClick={() => handleDeleteClick(apiKey.id, apiKey.name)}
                          className="p-1.5 text-muted-foreground hover:text-destructive hover:bg-destructive/10 rounded-md transition-colors"
                          title={t('api_keys.delete_title')}
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                        </button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>

      {/* Reveal API Key Modal */}
      {showRevealModal && revealingApiKey && (
        <div
          className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
          onClick={e => { if (e.target === e.currentTarget) closeRevealModal(); }}
          onKeyDown={e => e.key === 'Escape' && closeRevealModal()}
          tabIndex={-1}
        >
          <div className="bg-card border border-border rounded-lg shadow-lg max-w-md w-full" onClick={e => e.stopPropagation()}>
            <div className="p-6">
              <div className="flex items-center gap-3 mb-5">
                <div className="p-2.5 rounded-xl bg-primary/10">
                  <Key className="h-5 w-5 text-primary" />
                </div>
                <div className="flex-1">
                  <h3 className="text-lg font-semibold text-foreground tracking-[-0.04em]">
                    {revealedFullKey ? t('api_keys.revealed_title') : t('api_keys.reveal_title')}
                  </h3>
                  <p className="text-sm text-muted-foreground mt-0.5">
                    {revealedFullKey ? t('api_keys.revealed_subtitle') : t('api_keys.reveal_subtitle', { name: revealingApiKey.name })}
                  </p>
                </div>
                <button
                  onClick={closeRevealModal}
                  className="p-1.5 text-muted-foreground hover:text-foreground hover:bg-muted rounded-lg transition-colors"
                >
                  <X className="h-4 w-4" />
                </button>
              </div>

              {revealedFullKey ? (
                <div className="space-y-5">
                  <div>
                    <label className="block text-sm font-medium text-foreground mb-2">
                      {t('api_keys.full_key')}
                    </label>
                    <div className="flex items-center gap-2">
                      <code className="flex-1 px-4 py-3 bg-muted border border-border rounded-xl text-sm font-mono break-all select-all text-foreground">
                        {revealedFullKey}
                      </code>
                      <button
                        onClick={() => copyToClipboard(revealedFullKey)}
                        className="p-2.5 text-muted-foreground hover:text-foreground hover:bg-muted rounded-lg transition-colors"
                        title={t('api_keys.copy_key')}
                      >
                        <Copy className="h-4 w-4" />
                      </button>
                    </div>
                  </div>

                  <div className="flex justify-end gap-2">
                    <button
                      onClick={() => setConfigExport({ apiKey: revealedFullKey, isRealKey: true, initialMode: 'files' })}
                      className="px-4 py-2.5 text-sm font-medium bg-card border border-border text-foreground rounded-xl hover:bg-muted transition-colors flex items-center gap-2"
                    >
                      <FileCode className="h-4 w-4" />
                      {t('api_keys.config_export.export_button')}
                    </button>
                    <button
                      onClick={() => copyToClipboard(revealedFullKey)}
                      className="px-4 py-2.5 text-sm font-medium bg-primary text-primary-foreground rounded-xl hover:bg-primary/90 transition-colors flex items-center gap-2"
                    >
                      <Copy className="h-4 w-4" />
                      {t('common:copy')}
                    </button>
                  </div>
                </div>
              ) : (
                <div className="space-y-5">
                  <div>
                    <label className="block text-sm font-medium text-foreground mb-2">
                      {t('api_keys.account_password')}
                    </label>
                    <input
                      type="password"
                      value={revealPassword}
                      onChange={(e) => setRevealPassword(e.target.value)}
                      onKeyDown={(e) => e.key === 'Enter' && !revealing && revealFullKey()}
                      placeholder={t('api_keys.account_password_placeholder')}
                      className="w-full px-4 py-2.5 border border-border rounded-xl bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent transition-all"
                      autoFocus
                    />
                    <div className="mt-3 p-3 bg-amber-500/10 border border-amber-500/20 rounded-xl">
                      <div className="flex items-start gap-2.5">
                        <AlertTriangle className="h-5 w-5 text-amber-500 flex-shrink-0 mt-0.5" />
                        <p className="text-sm text-amber-600/80 dark:text-amber-400/80">
                          {t('api_keys.reveal_warning')}
                        </p>
                      </div>
                    </div>
                  </div>

                  <div className="flex gap-3 justify-end">
                    <button
                      onClick={closeRevealModal}
                      disabled={revealing}
                      className="px-4 py-2.5 text-sm font-medium text-muted-foreground hover:text-foreground rounded-xl hover:bg-muted transition-colors disabled:opacity-50"
                    >
                      {t('common:cancel')}
                    </button>
                    <button
                      onClick={revealFullKey}
                      disabled={revealing || !revealPassword}
                      className="px-4 py-2.5 text-sm font-medium bg-primary text-primary-foreground rounded-xl hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center gap-2"
                    >
                      {revealing ? (
                        <>
                          <div className="w-4 h-4 border-2 border-primary-foreground border-t-transparent rounded-full animate-spin"></div>
                          {t('api_keys.revealing')}
                        </>
                      ) : (
                        <>
                          <Key className="h-4 w-4" />
                          {t('api_keys.reveal_button')}
                        </>
                      )}
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* API Usage Tutorial */}
      <div className="mt-8 space-y-6">
        {/* Authentication Info */}
        <div className="rounded-lg border border-border bg-card p-5 shadow-xs">
          <div className="flex items-start gap-3.5">
            <div className="p-2.5 rounded-lg bg-primary/10">
              <Key className="h-5 w-5 text-primary" />
            </div>
            <div className="flex-1">
              <h3 className="text-[15px] font-semibold text-foreground tracking-[-0.02em] mb-1.5">
                {t('api_keys.auth_title')}
              </h3>
              <p className="text-[13px] text-muted-foreground mb-3">
                {t('api_keys.auth_desc')}
              </p>
              <div className="space-y-3">
                <div>
                  <label className="text-[12px] font-medium text-muted-foreground uppercase tracking-wider mb-1.5 block">
                    {t('api_keys.base_url_label')}
                  </label>
                  <div className="relative">
                    <div className="bg-muted border border-border rounded-lg pl-4 pr-12 py-3">
                      <code className="text-[13px] font-mono text-foreground">
                        https://api.etherworks.cn/v1
                      </code>
                    </div>
                    <button
                      onClick={() => copyToClipboard('https://api.etherworks.cn/v1')}
                      className="absolute right-2 top-1/2 -translate-y-1/2 p-2 text-muted-foreground hover:text-foreground hover:bg-background rounded-md transition-colors"
                      title={t('common:copy')}
                    >
                      <Copy className="h-4 w-4" />
                    </button>
                  </div>
                </div>
                <div className="flex flex-wrap gap-2">
                  <button
                    onClick={() => setConfigExport({ apiKey: CONFIG_API_KEY_PLACEHOLDER, isRealKey: false, initialMode: 'files' })}
                    className="flex items-center gap-2 px-4 py-2.5 bg-card border border-border rounded-lg hover:bg-muted transition-colors text-[13px] font-medium text-foreground"
                  >
                    <FileCode className="h-4 w-4 text-primary" />
                    {t('api_keys.config_export.button_view')}
                  </button>
                  <button
                    onClick={() => setConfigExport({ apiKey: CONFIG_API_KEY_PLACEHOLDER, isRealKey: false, initialMode: 'script' })}
                    className="flex items-center gap-2 px-4 py-2.5 bg-card border border-border rounded-lg hover:bg-muted transition-colors text-[13px] font-medium text-foreground"
                  >
                    <Terminal className="h-4 w-4 text-primary" />
                    {t('api_keys.config_export.button_script')}
                  </button>
                </div>
                <div>
                  <label className="text-[12px] font-medium text-muted-foreground uppercase tracking-wider mb-1.5 block">
                    {t('api_keys.models_title')}
                  </label>
                  {modelsLoading ? (
                    <div className="flex items-center justify-center py-10">
                      <div className="w-6 h-6 border-2 border-primary border-t-transparent rounded-full animate-spin"></div>
                    </div>
                  ) : models.length === 0 ? (
                    <div className="text-center py-10 rounded-xl border-2 border-dashed border-border">
                      <div className="w-12 h-12 bg-muted rounded-xl flex items-center justify-center mx-auto mb-3">
                        <Cpu className="h-6 w-6 text-muted-foreground" />
                      </div>
                      <p className="text-[13px] text-muted-foreground">{t('api_keys.no_models')}</p>
                    </div>
                  ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
                      {models.map((model) => {
                        const brandIcon = getModelBrandIcon(model.id);
                        const contextSize = model.contextWindow || model.context_length;
                        const formattedContext = contextSize
                          ? contextSize >= 1_000_000
                            ? `${(contextSize / 1_000_000).toFixed(contextSize % 1_000_000 === 0 ? 0 : 1)}M`
                            : contextSize >= 1_000
                              ? `${Math.round(contextSize / 1_000)}K`
                              : String(contextSize)
                          : null;

                        const inputMods = model.input_modalities || [];
                        const outputMods = model.output_modalities || [];
                        const hasVision = inputMods.includes('image');
                        const hasVideo = inputMods.includes('video');
                        const hasFileUpload = inputMods.includes('file');
                        const hasImageGen = outputMods.includes('image');

                        return (
                          <div
                            key={model.uuid}
                            className="flex items-start justify-between p-4 bg-muted/50 rounded-xl group hover:bg-muted transition-colors"
                          >
                            <div className="flex items-start gap-4 min-w-0 flex-1">
                              <div className={`p-3 rounded-xl shadow-sm ${brandIcon ? 'bg-white' : 'bg-card'}`}>
                                {brandIcon ? (
                                  <img src={brandIcon} alt="" className="h-6 w-6 object-contain" />
                                ) : (
                                  <Cpu className="h-6 w-6 text-purple-500" />
                                )}
                              </div>
                              <div className="min-w-0 flex-1">
                                <div className="flex items-center gap-2">
                                  <span className="font-semibold text-lg text-foreground truncate">{model.id}</span>
                                  <button
                                    onClick={() => copyToClipboard(model.id)}
                                    className="p-1 text-muted-foreground/50 hover:text-primary rounded transition-colors opacity-0 group-hover:opacity-100 flex-shrink-0"
                                    title={t('api_keys.copy_model_id')}
                                  >
                                    <Copy className="h-3 w-3" />
                                  </button>
                                </div>
                                <div className="flex items-center gap-1.5 mt-1.5">
                                  <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 whitespace-nowrap">
                                    <Type className="h-3 w-3" /> Text
                                  </span>
                                  {hasVision && (
                                    <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-xs font-medium bg-emerald-100 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-300 whitespace-nowrap">
                                      <Eye className="h-3 w-3" /> Vision
                                    </span>
                                  )}
                                  {hasFileUpload && (
                                    <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-xs font-medium bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300 whitespace-nowrap">
                                      <FileText className="h-3 w-3" /> File
                                    </span>
                                  )}
                                  {hasVideo && (
                                    <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-xs font-medium bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300 whitespace-nowrap">
                                      <Video className="h-3 w-3" /> Video
                                    </span>
                                  )}
                                  {hasImageGen && (
                                    <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-xs font-medium bg-emerald-100 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-300 whitespace-nowrap">
                                      <ImageIcon className="h-3 w-3" /> Image Gen
                                    </span>
                                  )}
                                </div>
                                {formattedContext && (
                                  <div className="flex items-center gap-3 mt-2 text-sm text-muted-foreground">
                                    <span className="inline-flex items-center gap-1.5 font-medium whitespace-nowrap text-primary">
                                      <Layers className="h-4 w-4" />
                                      {formattedContext}
                                    </span>
                                  </div>
                                )}
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Code Examples */}
        <div className="rounded-lg border border-border bg-card shadow-xs overflow-hidden">
          {/* Header */}
          <div className="p-5 border-b border-border">
            <div className="flex items-center gap-3.5">
              <div className="p-2.5 rounded-lg bg-primary/10">
                <Code className="h-5 w-5 text-primary" />
              </div>
              <div>
                <h3 className="text-[15px] font-semibold text-foreground tracking-[-0.02em]">
                  {t('api_keys.examples_title')}
                </h3>
                <p className="text-[13px] text-muted-foreground mt-0.5">
                  {t('api_keys.examples_subtitle')}
                </p>
              </div>
            </div>
          </div>

          {/* Language Tabs */}
          <div className="px-5 pt-5">
            <div className="text-[12px] font-medium text-muted-foreground uppercase tracking-wider mb-3">
              {t('api_keys.language')}
            </div>
            <div className="inline-flex items-center bg-muted p-1 rounded-xl border border-border/50 mb-5">
              <button
                onClick={() => setActiveLanguageTab('curl')}
                className={`flex items-center gap-2 py-1.5 px-4 rounded-lg text-[13px] font-medium transition-all ${
                  activeLanguageTab === 'curl'
                    ? 'bg-background text-foreground shadow-sm'
                    : 'text-muted-foreground hover:text-foreground hover:bg-background/50'
                }`}
              >
                <div className="w-5 h-5 bg-slate-500/20 rounded flex items-center justify-center">
                  <span className="text-slate-300 font-bold text-[8px]">C</span>
                </div>
                cURL
              </button>
              <button
                onClick={() => setActiveLanguageTab('nodejs')}
                className={`flex items-center gap-2 py-1.5 px-4 rounded-lg text-[13px] font-medium transition-all ${
                  activeLanguageTab === 'nodejs'
                    ? 'bg-background text-foreground shadow-sm'
                    : 'text-muted-foreground hover:text-foreground hover:bg-background/50'
                }`}
              >
                <div className="w-5 h-5 bg-green-500/20 rounded flex items-center justify-center">
                  <span className="text-green-500 font-bold text-[9px]">{t('common:js')}</span>
                </div>
                Node.js
              </button>
              <button
                onClick={() => setActiveLanguageTab('typescript')}
                className={`flex items-center gap-2 py-1.5 px-4 rounded-lg text-[13px] font-medium transition-all ${
                  activeLanguageTab === 'typescript'
                    ? 'bg-background text-foreground shadow-sm'
                    : 'text-muted-foreground hover:text-foreground hover:bg-background/50'
                }`}
              >
                <div className="w-5 h-5 bg-blue-500/20 rounded flex items-center justify-center">
                  <span className="text-blue-500 font-bold text-[9px]">{t('common:ts')}</span>
                </div>
                TypeScript
              </button>
              <button
                onClick={() => setActiveLanguageTab('python')}
                className={`flex items-center gap-2 py-1.5 px-4 rounded-lg text-[13px] font-medium transition-all ${
                  activeLanguageTab === 'python'
                    ? 'bg-background text-foreground shadow-sm'
                    : 'text-muted-foreground hover:text-foreground hover:bg-background/50'
                }`}
              >
                <div className="w-5 h-5 bg-yellow-500/20 rounded flex items-center justify-center">
                  <span className="text-yellow-500 font-bold text-[9px]">{t('common:py')}</span>
                </div>
                Python
              </button>
              <button
                onClick={() => setActiveLanguageTab('python_sdk')}
                className={`flex items-center gap-2 py-1.5 px-4 rounded-lg text-[13px] font-medium transition-all ${
                  activeLanguageTab === 'python_sdk'
                    ? 'bg-background text-foreground shadow-sm'
                    : 'text-muted-foreground hover:text-foreground hover:bg-background/50'
                }`}
              >
                <div className="w-5 h-5 bg-emerald-500/20 rounded flex items-center justify-center">
                  <span className="text-emerald-500 font-bold text-[8px]">{t('common:ai')}</span>
                </div>
                {t('api_keys.python_sdk')}
              </button>
            </div>
          </div>

          {/* Example Type Tabs */}
          <div className="px-5 pb-5">
            <div className="text-[12px] font-medium text-muted-foreground uppercase tracking-wider mb-3">
              {t('api_keys.implementation')}
            </div>
            <div className="inline-flex items-center bg-muted p-1 rounded-xl border border-border/50 mb-5">
              <button
                onClick={() => setActiveExampleTab('basic')}
                className={`flex items-center gap-1.5 py-1.5 px-3.5 rounded-lg text-[13px] font-medium transition-all ${
                  activeExampleTab === 'basic'
                    ? 'bg-background text-foreground shadow-sm'
                    : 'text-muted-foreground hover:text-foreground hover:bg-background/50'
                }`}
              >
                💬 {t('api_keys.basic_chat')}
              </button>
              <button
                onClick={() => setActiveExampleTab('streaming')}
                className={`flex items-center gap-1.5 py-1.5 px-3.5 rounded-lg text-[13px] font-medium transition-all ${
                  activeExampleTab === 'streaming'
                    ? 'bg-background text-foreground shadow-sm'
                    : 'text-muted-foreground hover:text-foreground hover:bg-background/50'
                }`}
              >
                ⚡ {t('api_keys.streaming')}
              </button>
              <button
                onClick={() => setActiveExampleTab('conversation')}
                className={`flex items-center gap-1.5 py-1.5 px-3.5 rounded-lg text-[13px] font-medium transition-all ${
                  activeExampleTab === 'conversation'
                    ? 'bg-background text-foreground shadow-sm'
                    : 'text-muted-foreground hover:text-foreground hover:bg-background/50'
                }`}
              >
                🗣️ {t('api_keys.conversation_tab')}
              </button>
            </div>
          </div>

          {/* Code Content */}
          <div className="border-t border-border">
            <div className="p-5">
              <div className="bg-zinc-950 rounded-xl overflow-hidden border border-zinc-800">
                {/* Code Header */}
                <div className="flex items-center justify-between px-4 py-2.5 bg-zinc-900 border-b border-zinc-800">
                  <div className="flex items-center gap-2.5">
                    <div className="flex items-center gap-1.5">
                      <div className="w-3 h-3 rounded-full bg-[#ff5f57]"></div>
                      <div className="w-3 h-3 rounded-full bg-[#febc2e]"></div>
                      <div className="w-3 h-3 rounded-full bg-[#28c840]"></div>
                    </div>
                    <span className="text-[13px] font-medium text-zinc-400 ml-1">
                      {activeLanguageTab === 'curl' ? 'cURL' :
                       activeLanguageTab === 'nodejs' ? 'Node.js' :
                       activeLanguageTab === 'typescript' ? 'TypeScript' :
                       activeLanguageTab === 'python_sdk' ? 'Python (OpenAI SDK)' : 'Python'} — {activeExampleTab === 'basic' ? t('api_keys.basic_chat') :
                        activeExampleTab === 'streaming' ? t('api_keys.streaming') : t('api_keys.conversation_tab')}
                    </span>
                  </div>
                  <button
                    onClick={() => {
                      const code = codeExamples[activeLanguageTab]?.[activeExampleTab];
                      if (code) copyToClipboard(code);
                    }}
                    className="text-zinc-500 hover:text-zinc-300 transition-colors p-1"
                  >
                    <Copy className="h-3.5 w-3.5" />
                  </button>
                </div>

                {/* Code Block */}
                <pre className="text-zinc-300 p-5 text-[13px] overflow-x-auto font-mono leading-relaxed">
                  {codeExamples[activeLanguageTab]?.[activeExampleTab] || t('api_keys.example_not_found')}
                </pre>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* View API Key Modal */}
      {showViewModal && selectedApiKey && (
        <div
          className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4 overflow-y-auto overscroll-contain"
          onClick={e => { if (e.target === e.currentTarget) setShowViewModal(false); }}
          onKeyDown={e => e.key === 'Escape' && setShowViewModal(false)}
          tabIndex={-1}
        >
          <div className="bg-card border border-border rounded-lg shadow-lg max-w-3xl w-full max-h-[90vh] overflow-hidden" onClick={e => e.stopPropagation()}>
            {/* Header */}
            <div className="p-5 border-b border-border">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="p-2.5 rounded-xl bg-primary/10">
                    <Key className="h-5 w-5 text-primary" />
                  </div>
                  <div>
                    <h2 className="text-lg font-semibold text-foreground tracking-[-0.04em]">{t('api_keys.details_title')}</h2>
                    <p className="text-[13px] text-muted-foreground mt-0.5">
                      {t('api_keys.details_subtitle')}
                    </p>
                  </div>
                </div>
                <button
                  onClick={() => setShowViewModal(false)}
                  className="p-1.5 text-muted-foreground hover:text-foreground hover:bg-muted rounded-lg transition-colors"
                >
                  <X className="h-4 w-4" />
                </button>
              </div>
            </div>

            {/* Content */}
            <div className="p-5 max-h-[calc(90vh-80px)] overflow-y-auto overscroll-contain">
              <div className="space-y-6">
                {/* Usage Statistics */}
                <div>
                  <div className="flex items-center gap-2.5 mb-4">
                    <BarChart3 className="h-4 w-4 text-primary" />
                    <h3 className="text-[15px] font-semibold text-foreground tracking-[-0.02em]">{t('api_keys.usage_statistics')}</h3>
                    <span className="text-[12px] text-muted-foreground">{t('api_keys.usage_subtitle')}</span>
                  </div>

                  <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
                    <div className="bg-card rounded-xl p-4 border border-border text-center">
                      <div className="w-9 h-9 bg-blue-500/10 rounded-xl flex items-center justify-center mx-auto mb-2.5">
                        <Activity className="h-4 w-4 text-blue-500" />
                      </div>
                      <div className="text-lg font-bold text-blue-500 mb-0.5">
                        {formatCompactNumber(selectedApiKey.usage?.totalRequests || 0)}
                      </div>
                      <div className="text-[11px] font-medium text-muted-foreground uppercase tracking-wider">{t('api_keys.total_requests')}</div>
                    </div>

                    <div className="bg-card rounded-xl p-4 border border-border text-center">
                      <div className="w-9 h-9 bg-emerald-500/10 rounded-xl flex items-center justify-center mx-auto mb-2.5">
                        <Calendar className="h-4 w-4 text-emerald-500" />
                      </div>
                      <div className="text-lg font-bold text-emerald-500 mb-0.5">
                        {formatCompactNumber(selectedApiKey.usage?.monthlyRequests || 0)}
                      </div>
                      <div className="text-[11px] font-medium text-muted-foreground uppercase tracking-wider">{t('api_keys.this_month')}</div>
                    </div>

                    <div className="bg-card rounded-xl p-4 border border-border text-center">
                      <div className="w-9 h-9 bg-purple-500/10 rounded-xl flex items-center justify-center mx-auto mb-2.5">
                        <Clock className="h-4 w-4 text-purple-500" />
                      </div>
                      <div className="text-lg font-bold text-purple-500 mb-0.5">
                        {formatCompactNumber(selectedApiKey.usage?.dailyRequests || 0)}
                      </div>
                      <div className="text-[11px] font-medium text-muted-foreground uppercase tracking-wider">{t('api_keys.today_label')}</div>
                    </div>

                    <div className="bg-card rounded-xl p-4 border border-border text-center">
                      <div className="w-9 h-9 bg-amber-500/10 rounded-xl flex items-center justify-center mx-auto mb-2.5">
                        <BarChart3 className="h-4 w-4 text-amber-500" />
                      </div>
                      <div className="text-lg font-bold text-amber-500 mb-0.5">
                        {formatCompactNumber(selectedApiKey.tokenUsage.totalTokens)}
                      </div>
                      <div className="text-[11px] font-medium text-muted-foreground uppercase tracking-wider">{t('api_keys.token_usage')}</div>
                    </div>
                  </div>
                </div>

                {/* Recent Activity */}
                <div>
                  <div className="flex items-center gap-2.5 mb-4">
                    <Activity className="h-4 w-4 text-emerald-500" />
                    <h3 className="text-[15px] font-semibold text-foreground tracking-[-0.02em]">{t('api_keys.recent_activity')}</h3>
                    <span className="text-[12px] text-muted-foreground">{t('api_keys.recent_subtitle')}</span>
                  </div>

                  {selectedApiKey.usageLogs && selectedApiKey.usageLogs.length > 0 ? (
                    <div>
                      <div className="space-y-3 mb-4">
                        {(() => {
                          const reversedLogs = selectedApiKey.usageLogs.slice().reverse();
                          const startIndex = (usageLogsPage - 1) * usageLogsPerPage;
                          const endIndex = startIndex + usageLogsPerPage;
                          const paginatedLogs = reversedLogs.slice(startIndex, endIndex);

                          return paginatedLogs.map((log: UsageLog, index: number) => (
                            <div key={`${usageLogsPage}-${index}`} className="bg-card rounded-xl p-4 border border-border">
                              <div className="flex items-start justify-between mb-3">
                                <div className="flex items-center gap-2.5">
                                  <div className="p-1.5 bg-primary/10 rounded-lg">
                                    <Info className="h-3.5 w-3.5 text-primary" />
                                  </div>
                                  <div>
                                    <div className="text-[13px] font-semibold text-foreground">
                                      {log.model}
                                    </div>
                                    <div className="text-[12px] text-muted-foreground">
                                      {formatDateTime(log.timestamp)}
                                    </div>
                                  </div>
                                </div>
                                <div className="text-right">
                                  <div className="text-[15px] font-bold text-emerald-500">
                                    {formatCompactNumber(log.usage.total_tokens)}
                                  </div>
                                  <div className="text-[11px] text-muted-foreground">{t('common:tokens')}</div>
                                </div>
                              </div>

                              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                <div className="bg-muted/50 rounded-lg p-3">
                                  <div className="text-[11px] font-semibold text-muted-foreground uppercase tracking-wider mb-2">{t('api_keys.token_usage')}</div>
                                  <div className="space-y-1 text-[13px]">
                                    <div className="flex justify-between">
                                      <span className="text-muted-foreground">{t('api_keys.input_label')}</span>
                                      <span className="font-medium text-foreground">
                                        {formatCompactNumber(log.usage.prompt_tokens)}
                                      </span>
                                    </div>
                                    <div className="flex justify-between">
                                      <span className="text-muted-foreground">{t('api_keys.output_label')}</span>
                                      <span className="font-medium text-foreground">
                                        {formatCompactNumber(log.usage.completion_tokens)}
                                      </span>
                                    </div>
                                    {log.usage.completion_tokens_details.reasoning_tokens > 0 && (
                                      <div className="flex justify-between">
                                        <span className="text-muted-foreground">{t('api_keys.reasoning_label')}</span>
                                        <span className="font-medium text-purple-500">
                                          {formatCompactNumber(log.usage.completion_tokens_details.reasoning_tokens)}
                                        </span>
                                      </div>
                                    )}
                                    {log.usage.prompt_tokens_details.cached_tokens > 0 && (
                                      <div className="flex justify-between">
                                        <span className="text-muted-foreground">{t('api_keys.read_cache_label')}</span>
                                        <span className="font-medium text-emerald-500">
                                          {formatCompactNumber(log.usage.prompt_tokens_details.cached_tokens)}
                                        </span>
                                      </div>
                                    )}
                                    <div className="flex justify-between border-t border-border pt-1 mt-2">
                                      <span className="text-muted-foreground font-medium">{t('api_keys.total_label')}</span>
                                      <span className="font-bold text-foreground">
                                        {formatCompactNumber(log.usage.total_tokens)}
                                      </span>
                                    </div>
                                  </div>
                                </div>

                                {log.reference && (
                                  <div className="bg-muted/50 rounded-lg p-3">
                                    <div className="text-[11px] font-semibold text-muted-foreground uppercase tracking-wider mb-2">{t('api_keys.reference')}</div>
                                    <div className="font-mono text-[13px] text-foreground break-all">
                                      {(() => {
                                        const match = log.reference.match(/^req_(\d+)$/);
                                        if (match) return formatDateTime(new Date(Number(match[1])).toISOString());
                                        return log.reference.length > 25 ? `${log.reference.substring(0, 25)}...` : log.reference;
                                      })()}
                                    </div>
                                  </div>
                                )}
                              </div>
                            </div>
                          ));
                        })()}
                      </div>

                      {/* Pagination Controls */}
                      {(() => {
                        const totalLogs = selectedApiKey.usageLogs.length;
                        const totalPages = Math.ceil(totalLogs / usageLogsPerPage);

                        if (totalPages <= 1) return null;

                        return (
                          <div className="flex items-center justify-between bg-muted/30 rounded-xl p-3 border border-border">
                            <button
                              onClick={() => setUsageLogsPage(prev => Math.max(prev - 1, 1))}
                              disabled={usageLogsPage === 1}
                              className="flex items-center gap-1.5 px-3 py-1.5 text-[13px] text-muted-foreground rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-muted transition-colors font-medium"
                            >
                              <ChevronLeft className="h-3.5 w-3.5" />
                              {t('common:previous')}
                            </button>

                            <div className="flex items-center gap-1">
                              <span className="text-[13px] font-medium text-muted-foreground">
                                {t('common:page_of', { current: usageLogsPage, total: totalPages })}
                              </span>
                              <div className="flex gap-1 ml-2">
                                {Array.from({ length: Math.min(totalPages, 5) }, (_, i) => {
                                  const pageNum = i + 1;
                                  return (
                                    <button
                                      key={pageNum}
                                      onClick={() => setUsageLogsPage(pageNum)}
                                      className={`w-7 h-7 text-[13px] font-medium rounded-lg transition-colors ${
                                        usageLogsPage === pageNum
                                          ? 'bg-primary text-primary-foreground'
                                          : 'text-muted-foreground hover:bg-muted'
                                      }`}
                                    >
                                      {pageNum}
                                    </button>
                                  );
                                })}
                              </div>
                            </div>

                            <button
                              onClick={() => setUsageLogsPage(prev => Math.min(prev + 1, totalPages))}
                              disabled={usageLogsPage === totalPages}
                              className="flex items-center gap-1.5 px-3 py-1.5 text-[13px] text-muted-foreground rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-muted transition-colors font-medium"
                            >
                              {t('common:next')}
                              <ChevronRight className="h-3.5 w-3.5" />
                            </button>
                          </div>
                        );
                      })()}
                    </div>
                  ) : (
                    <div className="text-center py-10 rounded-xl border-2 border-dashed border-border">
                      <div className="w-12 h-12 bg-muted rounded-xl flex items-center justify-center mx-auto mb-3">
                        <Activity className="h-6 w-6 text-muted-foreground" />
                      </div>
                      <h3 className="text-[15px] font-semibold text-foreground tracking-[-0.02em] mb-1">{t('api_keys.no_activity')}</h3>
                      <p className="text-[13px] text-muted-foreground max-w-md mx-auto">
                        {t('api_keys.no_activity_desc')}
                      </p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Edit API Key Modal */}
      {showEditModal && selectedApiKey && (
        <div
          className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4 overflow-y-auto overscroll-contain"
          onClick={e => { if (e.target === e.currentTarget) setShowEditModal(false); }}
          onKeyDown={e => e.key === 'Escape' && setShowEditModal(false)}
          tabIndex={-1}
        >
          <div className="bg-card border border-border rounded-lg shadow-lg max-w-lg w-full" onClick={e => e.stopPropagation()}>
            {/* Header */}
            <div className="p-5 border-b border-border">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="p-2.5 rounded-xl bg-primary/10">
                    <Edit className="h-5 w-5 text-primary" />
                  </div>
                  <div>
                    <h2 className="text-lg font-semibold text-foreground tracking-[-0.04em]">{t('api_keys.edit_title')}</h2>
                    <p className="text-[13px] text-muted-foreground mt-0.5">
                      {t('api_keys.edit_subtitle')}
                    </p>
                  </div>
                </div>
                <button
                  onClick={() => setShowEditModal(false)}
                  className="p-1.5 text-muted-foreground hover:text-foreground hover:bg-muted rounded-lg transition-colors"
                >
                  <X className="h-4 w-4" />
                </button>
              </div>
            </div>

            {/* Content */}
            <div className="p-5 space-y-4">
              {/* Key Name */}
              <div>
                <label className="text-[12px] font-medium text-muted-foreground uppercase tracking-wider mb-2 block">{t('api_keys.key_name')}</label>
                <input
                  type="text"
                  value={editingName}
                  onChange={(e) => setEditingName(e.target.value)}
                  className="w-full px-4 py-2.5 border border-border rounded-xl bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent transition-all"
                  placeholder={t('api_keys.enter_name')}
                  maxLength={50}
                />
              </div>

              {/* Masked Key */}
              <div>
                <label className="text-[12px] font-medium text-muted-foreground uppercase tracking-wider mb-2 block">{t('api_keys.masked_key')}</label>
                <code className="text-[13px] bg-muted px-4 py-2.5 rounded-xl font-mono text-foreground block border border-border">
                  {selectedApiKey.maskedKey}
                </code>
              </div>

              {/* Status */}
              <div>
                <label className="text-[12px] font-medium text-muted-foreground uppercase tracking-wider mb-2 block">{t('api_keys.status_label')}</label>
                {selectedApiKey.expiresAt && new Date(selectedApiKey.expiresAt) < new Date() ? (
                  <div className="flex items-center gap-2 px-4 py-2.5 bg-destructive/5 border border-destructive/20 rounded-xl">
                    <div className="w-2 h-2 rounded-full bg-destructive"></div>
                    <span className="text-[13px] font-medium text-destructive">{t('api_keys.inactive_expired')}</span>
                    <span className="text-[12px] text-muted-foreground italic ml-1">{t('api_keys.expired_no_modify')}</span>
                  </div>
                ) : (
                  <div className="inline-flex items-center bg-muted p-1 rounded-xl border border-border/50">
                    <button
                      type="button"
                      onClick={() => setEditingStatus(true)}
                      className={`py-1.5 px-4 rounded-lg text-[13px] font-medium transition-all ${
                        editingStatus
                          ? 'bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 shadow-sm border border-emerald-500/20'
                          : 'text-muted-foreground hover:text-foreground'
                      }`}
                    >
                      {t('api_keys.active_label')}
                    </button>
                    <button
                      type="button"
                      onClick={() => setEditingStatus(false)}
                      className={`py-1.5 px-4 rounded-lg text-[13px] font-medium transition-all ${
                        !editingStatus
                          ? 'bg-background text-foreground shadow-sm'
                          : 'text-muted-foreground hover:text-foreground'
                      }`}
                    >
                      {t('api_keys.inactive_label')}
                    </button>
                  </div>
                )}
              </div>

              {/* Metadata row */}
              <div className="flex gap-4 flex-wrap text-[13px] text-muted-foreground pt-2">
                <div className="flex items-center gap-1.5">
                  <Calendar className="w-3.5 h-3.5" />
                  <span>{formatDateTime(selectedApiKey.createdAt)}</span>
                </div>
                {selectedApiKey.expiresAt && (
                  <div className="flex items-center gap-1.5 text-amber-500">
                    <AlertTriangle className="w-3.5 h-3.5" />
                    <span>{formatDateTime(selectedApiKey.expiresAt)}</span>
                  </div>
                )}
                {selectedApiKey.lastUsedAt && (
                  <div className="flex items-center gap-1.5">
                    <Clock className="w-3.5 h-3.5" />
                    <span>{formatDateTime(selectedApiKey.lastUsedAt)}</span>
                  </div>
                )}
              </div>
            </div>

            {/* Footer */}
            <div className="px-5 py-4 border-t border-border flex gap-3 justify-end">
              <button
                onClick={cancelEditing}
                disabled={saving}
                className="px-4 py-2.5 text-sm font-medium text-muted-foreground hover:text-foreground rounded-xl hover:bg-muted transition-colors disabled:opacity-50"
              >
                {t('common:cancel')}
              </button>
              <button
                onClick={saveChanges}
                disabled={saving || !editingName.trim()}
                className="px-4 py-2.5 text-sm font-medium bg-primary hover:bg-primary/90 text-primary-foreground rounded-xl disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center gap-2"
              >
                {saving ? (
                  <>
                    <div className="w-4 h-4 border-2 border-primary-foreground border-t-transparent rounded-full animate-spin"></div>
                    {t('common:saving')}
                  </>
                ) : (
                  t('api_keys.save_changes')
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      <ConfigExportModal
        open={!!configExport}
        onClose={() => setConfigExport(null)}
        apiKey={configExport?.apiKey ?? CONFIG_API_KEY_PLACEHOLDER}
        isRealKey={configExport?.isRealKey ?? false}
        initialMode={configExport?.initialMode}
        models={models}
        copyToClipboard={copyToClipboard}
      />

    </div>
  );
}
